// Typed client for /api/ai. Exposes plain CRUD + a streamMessage() helper that
// drives the SSE response from POST /conversations/{id}/messages.

import { request, tokens } from "./api";

export interface Citation {
  lesson_id: string;
  course_id: string;
  chunk_index: number;
  similarity_score: number;
  excerpt: string;
  lesson_title?: string | null;
  course_title?: string | null;
  course_slug?: string | null;
}

export interface AIMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  model?: string | null;
  created_at: string;
  citations: Citation[];
}

export interface Conversation {
  id: string;
  title: string;
  course_scope_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConversationDetail extends Conversation {
  messages: AIMessage[];
}

export const aiApi = {
  createConversation: (body: { title?: string; course_scope_id?: string | null }) =>
    request<Conversation>("/ai/conversations", { method: "POST", body }),
  list: () => request<Conversation[]>("/ai/conversations"),
  get: (id: string) => request<ConversationDetail>(`/ai/conversations/${id}`),
  delete: (id: string) =>
    request<void>(`/ai/conversations/${id}`, { method: "DELETE" }),
};

// --- Streaming ---
// We use fetch + ReadableStream rather than EventSource because EventSource
// only supports GET. The protocol is straight SSE: event:/data:/blank-line.

export interface StreamHandlers {
  onCitations?: (cites: Citation[]) => void;
  onToken?: (text: string) => void;
  onDone?: (info: { message_id: string }) => void;
  onError?: (detail: string) => void;
}

export async function streamMessage(
  conversationId: string,
  body: { content: string; lesson_id?: string | null; course_id?: string | null },
  handlers: StreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const token = tokens.getAccess();
  const res = await fetch(`/api/ai/conversations/${conversationId}/messages`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = j.detail || detail;
    } catch {
      /* ignore */
    }
    handlers.onError?.(detail);
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line.
    let frameEnd;
    while ((frameEnd = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, frameEnd);
      buffer = buffer.slice(frameEnd + 2);
      handleFrame(frame, handlers);
    }
  }
  // flush trailing frame if any
  if (buffer.trim()) handleFrame(buffer, handlers);
}

function handleFrame(frame: string, handlers: StreamHandlers): void {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of frame.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  if (!dataLines.length) return;
  const raw = dataLines.join("\n");
  try {
    const data = JSON.parse(raw);
    if (event === "token") handlers.onToken?.(data.text);
    else if (event === "citations") handlers.onCitations?.(data);
    else if (event === "done") handlers.onDone?.(data);
    else if (event === "error") handlers.onError?.(data.detail || "Error");
  } catch {
    // ignore malformed frames
  }
}
